import { Grid, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Box, Stack, MenuItem, Select, FormControl, InputLabel, Tooltip } from '@mui/material';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { useEffect, useRef, useState } from 'react';
import { Add, Delete, Edit, InfoOutlined } from '@mui/icons-material';
import AutocompleteComponent from './AutocompleteComponent';
import { useNotification } from './NotificationProvider';

// Minimum characters typed before an async preselectionSearchFn is called, to avoid
// firing a search request on every single keystroke of a one- or two-letter query.
const MIN_SEARCH_LENGTH = 2;
// How long to wait after the user stops typing before calling preselectionSearchFn.
const SEARCH_DEBOUNCE_MS = 300;

type PreselectionOption = {
    id: string | number;
    [key: string]: any; // Allow any additional properties
};

type EditableDataGridProps = {
    col: GridColDef[];
    rows: any[]; // Array of row objects
    editIsOn: boolean;
    formData: any; // Parent form data state
    setFormData: Function; // Parent form data setter
    dataPath: string; // Path to the array in formData (e.g., 'contact.contact_persons')
    preselectionIsOn?: boolean; // Enable preselection mode
    preselectionList?: PreselectionOption[]; // Dynamic array of options
    preselectionDisplayField?: string; // Which field to display in dropdown
    preselectionValueField?: string; // Which field to use as value (defaults to 'id')
    readOnlyFields?: string[]; // Fields that should be read-only in preselection mode
    // When provided, the "Add Row" preselection dialog renders a debounced search-as-you-type
    // box instead of a <Select> populated from the (potentially huge) preselectionList - the
    // function is called with the typed query and should resolve to a small, already-filtered
    // list of PreselectionOptions (e.g. a server-side search endpoint). preselectionList is
    // ignored while this is set.
    preselectionSearchFn?: (query: string) => Promise<PreselectionOption[]>;
    preselectionSearchLabel?: string; // Label for the search box; defaults to a generic prompt
    // Per-field override of the add/edit dialog input (e.g. a dependent select whose options
    // come from the parent form). Receives the current dialog values and a setter; return
    // null to hide the field for the current values.
    customFieldRenderers?: Record<string, (ctx: {
        column: GridColDef;
        label: React.ReactNode;
        disabled: boolean;
        values: any;
        setValue: (field: string, value: any) => void;
    }) => React.ReactNode>;
}

function EditableDataGrid({
    col,
    rows,
    editIsOn,
    formData,
    setFormData,
    dataPath,
    preselectionIsOn = false,
    preselectionList = [],
    preselectionDisplayField = 'name',
    preselectionValueField = 'id',
    readOnlyFields = [],
    preselectionSearchFn,
    preselectionSearchLabel,
    customFieldRenderers,
}: EditableDataGridProps) {

    const [editableColumns, setEditableColumns] = useState<GridColDef[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
    const [formDataLocal, setFormDataLocal] = useState<any>({});
    const [, setSelectedRow] = useState<any>(null);
    const [editingIndex, setEditingIndex] = useState<number>(-1);
    const [selectedRowIds, setSelectedRowIds] = useState<GridRowSelectionModel>([]);
    const [selectedPreselectionItem, setSelectedPreselectionItem] = useState<PreselectionOption | null>(null);
    const [searchOptions, setSearchOptions] = useState<PreselectionOption[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const notify = useNotification();

    // Ensure all rows have IDs
    const rowsWithIds = rows.map((row, index) => ({
        ...row,
        id: row.id || `row-${index}-${Date.now()}` // Generate ID if missing
    }));

    // Update columns when editIsOn or col changes
    useEffect(() => {
        const updatedColumns = col.map(column => ({
            ...column,
            editable: editIsOn && column.editable !== false && !preselectionIsOn // Disable editing when preselection is on
        }));

        setEditableColumns(updatedColumns);
    }, [editIsOn, col, preselectionIsOn]);

    // Helper functions
    const getNestedProperty = (obj: any, path: string) => {
        return path.split('.').reduce((current, prop) => current?.[prop], obj);
    };

    const setNestedProperty = (obj: any, path: string, value: any) => {
        const keys = path.split('.');
        const lastKey = keys.pop()!;
        const target = keys.reduce((current, key) => {
            if (!current[key]) current[key] = {};
            return current[key];
        }, obj);
        target[lastKey] = value;
        return obj;
    };

    const initializeFormData = (rowData?: any) => {
        const initialData: any = {};
        col.forEach(column => {
            if (column.field !== 'id' && column.field !== 'actions') {
                initialData[column.field] = rowData ? rowData[column.field] || '' : '';
            }
        });
        return initialData;
    };

    const handleAddRow = () => {
        setDialogMode('add');
        setFormDataLocal(initializeFormData());
        setSelectedRow(null);
        setEditingIndex(-1);
        setSelectedPreselectionItem(null);
        setDialogOpen(true);
    };

    const handleEditRow = () => {
        if (selectedRowIds.length === 0) {
            notify('Please select a row to edit', 'warning');
            return;
        }
        
        const selectedId = selectedRowIds[0];
        const selectedRowData = rowsWithIds.find(row => row.id === selectedId);
        const index = rowsWithIds.findIndex(row => row.id === selectedId);
        
        if (selectedRowData) {
            setDialogMode('edit');
            setFormDataLocal(initializeFormData(selectedRowData));
            setSelectedRow(selectedRowData);
            setEditingIndex(index);
            setDialogOpen(true);
        }
    };

    const handleDeleteRow = () => {
        if (selectedRowIds.length === 0) {
            notify('Please select a row to delete', 'warning');
            return;
        }

        if (window.confirm('Are you sure you want to delete the selected row(s)?')) {
            const currentData = getNestedProperty(formData, dataPath) || [];
            const updatedData = currentData.filter((item: any) => 
                !selectedRowIds.includes(item.id)
            );
            
            const newFormData = { ...formData };
            setNestedProperty(newFormData, dataPath, updatedData);
            setFormData(newFormData);
            setSelectedRowIds([]); // Clear selection
        }
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setFormDataLocal({});
        setSelectedRow(null);
        setEditingIndex(-1);
        setSelectedPreselectionItem(null);
        setSearchOptions([]);
        setSearchLoading(false);
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = null;
        }
    };

    // Debounced search-as-you-type handler for the async preselection picker
    // (see preselectionSearchFn). Skips the call entirely below MIN_SEARCH_LENGTH so typing
    // the first letter or two doesn't already trigger a request.
    const handlePreselectionSearchInput = (event: any, inputValue: string, reason: string) => {
        if (!preselectionSearchFn) return;
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        // 'reset' fires when a value is selected/cleared; don't re-search on that.
        if (reason === 'reset') return;

        const trimmed = (inputValue || '').trim();
        if (trimmed.length < MIN_SEARCH_LENGTH) {
            setSearchOptions([]);
            setSearchLoading(false);
            return;
        }

        searchDebounceRef.current = setTimeout(async () => {
            setSearchLoading(true);
            try {
                const results = await preselectionSearchFn(trimmed);
                setSearchOptions(results || []);
            } catch (error) {
                console.error('Error searching for preselection options:', error);
                setSearchOptions([]);
            } finally {
                setSearchLoading(false);
            }
        }, SEARCH_DEBOUNCE_MS);
    };

    const handlePreselectionSearchSelect = (event: any, newValue: any) => {
        if (newValue && typeof newValue === 'object') {
            handlePreselectionChange(newValue);
        } else {
            setSelectedPreselectionItem(null);
            setFormDataLocal({});
        }
    };

    const handleFormSubmit = () => {
    const currentData = getNestedProperty(formData, dataPath) || [];
    let updatedData;

    if (dialogMode === 'add') {
        let newRow;
        
        if (preselectionIsOn && selectedPreselectionItem) {
            // Use the selected preselection item - DON'T override the ID
            newRow = {
                ...selectedPreselectionItem,
                ...formDataLocal, // Allow overriding of editable fields
                // Keep the original ID from selectedPreselectionItem
                [preselectionValueField]: selectedPreselectionItem[preselectionValueField]
            };
        } else {
            // Normal add mode - generate new ID only when not using preselection
            newRow = {
                id: Date.now(),
                ...formDataLocal
            };
        }
        
        updatedData = [...currentData, newRow];
    } else {
        // Update existing row
        updatedData = currentData.map((item: any, index: number) => 
            index === editingIndex ? { ...item, ...formDataLocal } : item
        );
    }

    const newFormData = { ...formData };
    setNestedProperty(newFormData, dataPath, updatedData);
    setFormData(newFormData);
    
    handleDialogClose();
    };

    const handleInputChange = (field: string, value: any) => {
        setFormDataLocal((prev: any) => ({
            ...prev,
            [field]: value
        }));
    };

    const handlePreselectionChange = (selectedItem: PreselectionOption) => {
        setSelectedPreselectionItem(selectedItem);
        // Pre-fill the form with the selected item's data
        setFormDataLocal(initializeFormData(selectedItem));
    };

    // A GridColDef's `description` (already a standard MUI DataGrid prop, shown as the column
    // header's own tooltip in the grid itself) is reused here for the add/edit dialog's field
    // label, so setting it once covers both places - a small info icon next to the label shows
    // it on hover.
    const renderFieldLabel = (column: GridColDef): React.ReactNode => {
        const text = column.headerName || column.field;
        if (!column.description) return text;
        return (
            <Stack direction="row" component="span" alignItems="center" gap={0.5}>
                <span>{text}</span>
                <Tooltip title={column.description}>
                    <InfoOutlined fontSize="inherit" sx={{ verticalAlign: 'middle', color: 'action.active' }} />
                </Tooltip>
            </Stack>
        );
    };

    const renderFormField = (column: GridColDef) => {
        if (column.field === 'id' || column.field === 'actions') return null;

        // In preselection mode, only the fields naming *which* item is linked (readOnlyFields,
        // e.g. 'name') are locked - identity isn't reassignable via edit, that's what
        // remove-and-re-add is for. Every other field (position/rate/days/source, etc.) stays
        // editable in both add (after picking an item) and edit mode. Non-preselection grids are
        // unaffected - nothing is ever disabled there.
        const disabled = preselectionIsOn && readOnlyFields.includes(column.field);
        const label = renderFieldLabel(column);

        if (customFieldRenderers?.[column.field]) {
            return customFieldRenderers[column.field]({ column, label, disabled, values: formDataLocal, setValue: handleInputChange });
        }

        // A column declared as `type: 'singleSelect'` with `valueOptions` (the same standard
        // DataGrid column type used for its own inline cell editor) renders as a dropdown here
        // too, instead of the default free-text field.
        if (column.type === 'singleSelect' && Array.isArray(column.valueOptions)) {
            return (
                <FormControl key={column.field} fullWidth margin="normal" disabled={disabled} required={column.headerName?.includes('*')}>
                    <InputLabel>{label}</InputLabel>
                    <Select
                        label={label}
                        value={formDataLocal[column.field] || ''}
                        onChange={(e) => handleInputChange(column.field, e.target.value)}
                    >
                        {column.valueOptions.map((option: any) => {
                            const value = typeof option === 'object' ? option.value : option;
                            const optionLabel = typeof option === 'object' ? option.label : option;
                            return <MenuItem key={value} value={value}>{optionLabel}</MenuItem>;
                        })}
                    </Select>
                </FormControl>
            );
        }

        return (
            <TextField
                key={column.field}
                fullWidth
                margin="normal"
                label={label}
                value={formDataLocal[column.field] || ''}
                onChange={(e) => handleInputChange(column.field, e.target.value)}
                type={column.type === 'number' ? 'number' : column.type === 'date' ? 'date' : 'text'}
                required={column.headerName?.includes('*')}
                disabled={disabled}
                InputLabelProps={column.type === 'date' || !!column.description ? { shrink: true } : undefined}
            />
        );
    };

    const renderPreselectionDialog = () => {
        return (
            <Box>
                {preselectionSearchFn ? (
                    // Search-as-you-type box: calls preselectionSearchFn (server-side search)
                    // instead of preloading every option, so this stays fast with a large table.
                    <AutocompleteComponent
                        options={searchOptions}
                        value={selectedPreselectionItem}
                        handleChange={handlePreselectionSearchSelect}
                        isMulti={false}
                        label={preselectionSearchLabel || 'Search for an option'}
                        placeholder={`Type at least ${MIN_SEARCH_LENGTH} characters to search`}
                        getOptionLabel={(option: any) => (option ? String(option[preselectionDisplayField] ?? '') : '')}
                        isOptionEqualToValue={(option: any, val: any) => option?.[preselectionValueField] === val?.[preselectionValueField]}
                        onInputChange={handlePreselectionSearchInput}
                        loading={searchLoading}
                        filterOptions={(opts: any[]) => opts}
                        noOptionsText={`Type at least ${MIN_SEARCH_LENGTH} characters to search`}
                    />
                ) : (
                    /* Dropdown for selecting from preselection list */
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Select Option</InputLabel>
                        <Select
                            value={selectedPreselectionItem ? selectedPreselectionItem[preselectionValueField] : ''}
                            onChange={(e) => {
                                const selected = preselectionList.find(item =>
                                    item[preselectionValueField] === e.target.value
                                );
                                if (selected) {
                                    handlePreselectionChange(selected);
                                }
                            }}
                            label="Select Option"
                        >
                            {preselectionList.map((item) => (
                                <MenuItem
                                    key={item[preselectionValueField]}
                                    value={item[preselectionValueField]}
                                >
                                    {item[preselectionDisplayField]}
                                    {/* Show additional info if available */}
                                    {item.project_number && ` (${item.project_number})`}
                                    {item.short_name && item.short_name !== item[preselectionDisplayField] &&
                                     ` (${item.short_name})`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {/* Show all fields after selection, some will be read-only */}
                {selectedPreselectionItem && (
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        {col.map((column) => (
                            <Grid item xs={12} md={6} key={column.field}>
                                {renderFormField(column)}
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>
        );
    };

    return (  
        <Grid container spacing={2}>
            {/* Action Buttons */}
            <Grid item xs={12}>
                {editIsOn && (
                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={handleAddRow}
                            color="primary"
                        >
                            Add Row
                        </Button>
                        {/* For preselection grids, editing only makes sense if there's a field
                            beyond the read-only identity ones (e.g. Partners has just 'name',
                            nothing to edit; Experts has position/rate/days/source). */}
                        {(!preselectionIsOn || col.some((c) => c.field !== 'id' && c.field !== 'actions' && !readOnlyFields.includes(c.field))) && (
                            <Button
                                variant="outlined"
                                startIcon={<Edit />}
                                onClick={handleEditRow}
                                color="primary"
                                disabled={selectedRowIds.length !== 1}
                            >
                                Edit Selected
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            startIcon={<Delete />}
                            onClick={handleDeleteRow}
                            color="error"
                            disabled={selectedRowIds.length === 0}
                        >
                            Delete Selected
                        </Button>
                    </Stack>
                )}
            </Grid>

            {/* DataGrid */}
            <Grid item xs={12}>
                {rowsWithIds !== undefined && rowsWithIds.length > 0 ? (
                    <DataGrid
                        key={`datagrid-${editIsOn}-${preselectionIsOn}`}
                        rows={rowsWithIds}
                        columns={editableColumns}
                        editMode='row'
                        autoHeight
                        checkboxSelection={editIsOn}
                        getRowId={(row) => row.id}
                        rowSelectionModel={selectedRowIds}
                        onRowSelectionModelChange={(newSelection) => {
                            setSelectedRowIds(newSelection);
                        }}
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 10 },
                            },
                        }}
                        sx={{
                            '& .MuiDataGrid-columnHeaderTitle': {
                                fontWeight: 'bold',
                            },
                            ...((!editIsOn || preselectionIsOn) && {
                                '& .MuiDataGrid-cell': {
                                    backgroundColor: '#f5f5f5',
                                },
                            }),
                        }}
                        slots={{
                            noRowsOverlay: () => (
                                <Grid
                                    container
                                    alignItems="center"
                                    justifyContent="center"
                                    style={{ height: '100%' }}
                                >
                                    <p>No Data Available yet</p>
                                </Grid>
                            ),
                        }}
                        isCellEditable={() => editIsOn && !preselectionIsOn}
                    />
                ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <p>No Data Available yet</p>
                        {editIsOn && (
                            <Button
                                variant="outlined"
                                startIcon={<Add />}
                                onClick={handleAddRow}
                                sx={{ mt: 2 }}
                            >
                                Add First Row
                            </Button>
                        )}
                    </Box>
                )}
            </Grid>

            {/* Add/Edit Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleDialogClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {dialogMode === 'add' ? 
                        (preselectionIsOn ? 'Select and Add' : 'Add New Row') : 
                        'Edit Row'
                    }
                </DialogTitle>
                <DialogContent>
                    {preselectionIsOn && dialogMode === 'add' ? (
                        renderPreselectionDialog()
                    ) : (
                        <Grid container spacing={2}>
                            {col.map((column) => (
                                <Grid item xs={12} md={6} key={column.field}>
                                    {renderFormField(column)}
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="secondary">
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleFormSubmit} 
                        color="primary" 
                        variant="contained"
                        disabled={preselectionIsOn && dialogMode === 'add' && !selectedPreselectionItem}
                    >
                        {dialogMode === 'add' ? 'Add' : 'Update'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
}

export default EditableDataGrid;